import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Col, Modal, Row, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import api from "../../../../utils/api";
import socket from "../../../../context/socket";

const MFCentralModal = ({
  showMFCentralModal,
  onHideMFCentralModal,
  setShowQRUpoaderModal,
  roomId,
  getAllExternalFolios,
  onExternalPortfolioImported,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      panNumber: "",
      email_or_mobile: "",
    },
  });

  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null); // To show API errors
  const [otpError, setOtpError] = useState(null); // To show OTP errors
  const [alreadyFetched, setAlreadyFetched] = useState(false);

  const panNumber = watch("panNumber");
  const email_or_mobile = watch("email_or_mobile");

  // Track remote values to prevent echo loops
  const lastRemotePanRef = useRef("");
  const lastRemoteMobileRef = useRef("");
  // Track last sent values to avoid duplicate emits
  const lastSentPanRef = useRef("");
  const lastSentMobileRef = useRef("");

  // Sync panNumber local changes to remote
  useEffect(() => {
    const currentPan = panNumber || "";
    if (currentPan !== lastRemotePanRef.current && currentPan !== lastSentPanRef.current) {
      lastSentPanRef.current = currentPan;
      if (roomId) {
        socket.emit("mf_central_input", {
          room: roomId,
          data: {
            field: "panNumber",
            value: currentPan,
            panNumber: currentPan,
            email_or_mobile: email_or_mobile || "",
          },
        });
      }
    }
    setAlreadyFetched(false);
    if (roomId) {
      socket.emit("message_to", {
        room: roomId,
        data: {
          location: "mf_central_already_fetched",
          alreadyFetched: false,
        },
      });
    }
  }, [panNumber, roomId]);

  // Sync email_or_mobile local changes to remote
  useEffect(() => {
    const currentMobile = email_or_mobile || "";
    if (currentMobile !== lastRemoteMobileRef.current && currentMobile !== lastSentMobileRef.current) {
      lastSentMobileRef.current = currentMobile;
      if (roomId) {
        socket.emit("mf_central_input", {
          room: roomId,
          data: {
            field: "email_or_mobile",
            value: currentMobile,
            panNumber: panNumber || "",
            email_or_mobile: currentMobile,
          },
        });
      }
    }
  }, [email_or_mobile, roomId]);

  // Receive field updates from client
  useEffect(() => {
    if (!roomId) return;
    socket.emit("join", { room: roomId });

    const handleMessage = (msg) => {
      if (!msg?.data) return;
      const { field, value, panNumber: rPan, email_or_mobile: rMobile } = msg.data;

      if (field === "panNumber" && value !== undefined) {
        const cur = getValues("panNumber") || "";
        if (value !== cur) {
          lastRemotePanRef.current = value;
          setValue("panNumber", value, { shouldValidate: true, shouldDirty: true });
        }
      } else if (field === "email_or_mobile" && value !== undefined) {
        const cur = getValues("email_or_mobile") || "";
        if (value !== cur) {
          lastRemoteMobileRef.current = value;
          setValue("email_or_mobile", value, { shouldValidate: true, shouldDirty: true });
        }
      } else {
        if (rPan !== undefined) {
          const curPan = getValues("panNumber") || "";
          if (rPan !== curPan) {
            lastRemotePanRef.current = rPan;
            setValue("panNumber", rPan, { shouldValidate: true, shouldDirty: true });
          }
        }
        if (rMobile !== undefined) {
          const curMobile = getValues("email_or_mobile") || "";
          if (rMobile !== curMobile) {
            lastRemoteMobileRef.current = rMobile;
            setValue("email_or_mobile", rMobile, { shouldValidate: true, shouldDirty: true });
          }
        }
      }
    };

    socket.on("mf_central_input", handleMessage);

    return () => {
      socket.off("mf_central_input", handleMessage);
    };
  }, [roomId, setValue, getValues]);

  // Function to send OTP after verifying if portfolio is already fetched
  const sendMfcentralOTP = async () => {
    setLoading(true);
    setErrorMessage(null);
    setAlreadyFetched(false);

    Swal.fire({
      text: "Checking portfolio status...",
      imageUrl: "https://www.finedge.in/assets/images/download.gif",
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    try {
      // Step 1: Check if external investments were already fetched today for this PAN
      const cleanPan = panNumber ? String(panNumber).trim().toUpperCase() : "";
      const checkResponse = await api.post("advisor/checkExternalInvestments", {
        client_id: roomId,
        pan: cleanPan || undefined,
      });

      if (checkResponse.data?.Status === 200) {
        Swal.close();
        setLoading(false);
        setAlreadyFetched(true);
        if (roomId) {
          socket.emit("message_to", {
            room: roomId,
            data: {
              location: "mf_central_already_fetched",
              alreadyFetched: true,
              panNumber: cleanPan,
            },
          });
        }
        return;
      }

      // Step 2: Not fetched today -> proceed to generate OTP
      Swal.fire({
        text: "Generating OTP...",
        imageUrl: "https://www.finedge.in/assets/images/download.gif",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      await proceedToSendOtp();
    } catch (error) {
      console.log("Error in sendMfcentralOTP:", error);
      Swal.close();
      setLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to process request.");
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const proceedToSendOtp = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await api.post(
        `advisor/send_otp_mfcentral`,
        {
          clientId: roomId,
          mfcentralmob_email: email_or_mobile,
          mfcentraalpan: panNumber,
        }
      );

      console.log("OTP Response:", response);

      if (response.data.finalResponse && response.data.finalResponse.redirectUrl) {
        const url = response.data.finalResponse.redirectUrl;

        window.open(
          url,
          "_blank",
          "width=600,height=700,top=100,left=100,resizable=yes,scrollbars=yes"
        );

        socket.emit("join", { room: roomId });
        socket.emit("message_to", {
          room: roomId,
          data: {
            location: "mf_central_redirect_url",
            url: url
          },
        });
      }

      if (response.data.StatusCode === 400) {
        setErrorMessage(response.data.message);
        Swal.close();
      } else {
        // setOtpSent(true);
        setShowQRUpoaderModal(true);
        reset();
        onHideMFCentralModal();
        Swal.close();
      }
    } catch (error) {
      console.log('response', error);
      Swal.close();
      setErrorMessage(error.response?.data?.message || "Failed to send OTP.");
    } finally {
      Swal.close();
      setLoading(false);
      setTimeout(() => setErrorMessage(null), 5000); // Hide error after 5 seconds
    }
  };

  // Function to verify OTP
  const verifyMfcentralOTP = async (data) => {
    setOtpLoading(true);
    setOtpError(null);
    Swal.fire({
      text: "Please wait...",
      imageUrl: "https://www.finedge.in/assets/images/download.gif",
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    try {
      const response = await api.post(
        `advisor/verify_mfcentral_details`,
        {
          clientId: roomId,
          mfcentralotp: data.otp,
        }
      );

      if (response.data.StatusCode === 400) {
        setOtpError(response.data.message);
        Swal.close();
      } else {
        console.log("OTP Verified Successfully:", data.otp);
        onHideMFCentralModal();
        handleExternalInvestmentsClick(panNumber);
        reset();
      }
    } catch (error) {
      setOtpError("Invalid OTP. Please try again.");
      Swal.close();
    } finally {
      setOtpLoading(false);
      setTimeout(() => setOtpError(null), 5000);
    }
  };

  const handleExternalInvestmentsClick = async (pan) => {
    try {
      const cleanPan = String(pan || "")
        .trim()
        .toUpperCase();

      if (!cleanPan) {
        throw new Error("PAN number is missing");
      }
      const response = await api.post(
        `advisor/getMfcentralFolioData`,
        { client_id: roomId, pan: cleanPan, }
      );

      console.log("checkExternalInvestments", response);

      if (response?.data?.StatusCode == 200) {
        await getAllExternalFolios?.();
        await onExternalPortfolioImported?.(cleanPan);
      } else {
        Swal.close();
        Swal.fire({
          title: response?.data?.message || "No Data Found",
          icon: null,
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "swal-confirm-btn  ",
          },
        });
      }
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
      Swal.close();
    }
  };

  const handleClose = () => {
    reset();
    lastRemotePanRef.current = "";
    lastRemoteMobileRef.current = "";
    lastSentPanRef.current = "";
    lastSentMobileRef.current = "";
    setOtpSent(false);
    setLoading(false);
    setOtpLoading(false);
    setErrorMessage(null);
    setOtpError(null);
    setAlreadyFetched(false);
    onHideMFCentralModal();
    if (roomId) {
      socket.emit("message_to", {
        room: roomId,
        data: {
          location: "show_mfcentral_model",
          showMFCentralModal: false,
        },
      });
      socket.emit("message_to", {
        room: roomId,
        data: {
          location: "mf_central_already_fetched",
          alreadyFetched: false,
        },
      });
    }
  };

  return (
    <Modal
      show={showMFCentralModal}
      onHide={handleClose}
      className="custom-modal"
      backdropClassName="custom-modal-backdrop"
      style={{ zIndex: 1070 }}
    >
      <Modal.Header closeButton style={{ backgroundColor: "#fff", border: "none" }}>
        <Modal.Title>External Investment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Show API Error Message */}
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Form onSubmit={handleSubmit(verifyMfcentralOTP)}>
          {/* PAN Number */}
          <Row className="mt-3">
            <Col md={12}>
              <Form.Group controlId="panNumber">
                <Form.Label>PAN Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter PAN Number"
                  {...register("panNumber", {
                    required: "PAN Number is required",
                  })}
                />
                {errors.panNumber && <p className="error text-danger">{errors.panNumber.message}</p>}
              </Form.Group>
            </Col>
          </Row>

          {/* Mobile Number */}
          <Row className="mt-3">
            <Col md={12}>
              <Form.Group controlId="email_or_mobile">
                <Form.Label>Enter your registered email or mobile</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Email or Mobile"
                  {...register("email_or_mobile", {
                    required: "Mobile Number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Enter a valid 10-digit mobile number",
                    },
                  })}
                />
                {errors.email_or_mobile && <p className="error text-danger">{errors.email_or_mobile.message}</p>}
              </Form.Group>
            </Col>
          </Row>

          {/* If already fetched */}
          {alreadyFetched && (
            <Alert variant="success" className="mt-3">
              <div className="text-center p-2">
                <h6 className="font-weight-bold mb-2" style={{ color: "#00652d", fontWeight: 600 }}>
                  ✓ Portfolio Already Fetched Today
                </h6>
                <p className="text-muted small mb-3">
                  Holdings for this client were already fetched and saved. Click below to view the portfolio review.
                </p>
                <div className="d-flex flex-column gap-2">
                  <button
                    type="button"
                    className="lrgbutton text-white text-uppercase w-100 mb-2"
                    style={{ backgroundColor: "#00652d", borderColor: "#00652d" }}
                    onClick={async () => {
                      const cleanPan = String(panNumber || "").trim().toUpperCase();
                      setAlreadyFetched(false);
                      if (getAllExternalFolios) await getAllExternalFolios();
                      if (onExternalPortfolioImported) await onExternalPortfolioImported(cleanPan);
                      onHideMFCentralModal();
                    }}
                  >
                    Show Portfolio Result
                  </button>
                </div>
              </div>
            </Alert>
          )}

          {/* Generate OTP Button */}
          {!otpSent && !alreadyFetched && (
            <Row className="mt-3">
              <Col md={12} className="text-center">
                <button
                  type="button"
                  className="lrgbutton text-white text-uppercase w-100"
                  onClick={sendMfcentralOTP}
                  disabled={!panNumber || !email_or_mobile || loading}
                >
                  {loading ? <Spinner as="span" animation="border" size="sm" /> : "Generate OTP"}
                </button>
              </Col>
            </Row>
          )}

          {/* OTP Input - Shows Only If OTP is Sent */}
          {otpSent && (
            <>
              {otpError && <Alert variant="danger">{otpError}</Alert>}

              <Row className="mt-3">
                <Col md={12}>
                  <Form.Group controlId="otp">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter OTP"
                      {...register("otp", {
                        required: "OTP is required",
                        pattern: {
                          value: /^[0-9]{6}$/,
                          message: "Enter a valid 6-digit OTP",
                        },
                      })}
                    />
                    {errors.otp && <p className="error text-danger">{errors.otp.message}</p>}
                  </Form.Group>
                </Col>
              </Row>

              {/* Verify OTP Button */}
              <Row className="mt-4">
                <Col md={12} className="text-center">
                  <button className="lrgbutton text-white text-uppercase w-100" disabled={otpLoading}>
                    {otpLoading ? <Spinner as="span" animation="border" size="sm" /> : "Verify OTP"}
                  </button>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default MFCentralModal;
